import {
  deploymentCredentialSlots,
  effectiveDeploymentAddress,
} from '@/features/onboarding/deploymentAddress';
import { hasShellCapability } from './shellDetection';
import { readShellSession } from './shellSessionVault';
import {
  holdsCurrentRefreshSecret,
  removeCurrentRefreshSecret,
  restorePendingRefreshSecrets,
  writeCurrentRefreshSecret,
} from '@/services/refreshSecretWriteAhead';

export interface IShellHydrationOutcome {
  outcome: 'not-shell' | 'already-present' | 'restored' | 'replaced' | 'none-stored';
}

/**
 * Who an access token names — its `sub`, the person's id — or `null` for a token that is not a JWT
 * or names nobody. Read, never verified: it only decides which of two stored sessions is the
 * signed-in person's, never what the server allows.
 */
export function tokenOwner(token: string | null | undefined): string | null {
  const payload = token?.split('.')[1];
  if (!payload) return null;
  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const sub = (JSON.parse(json) as { sub?: unknown }).sub;
    return typeof sub === 'string' && sub !== '' ? sub : null;
  } catch {
    return null;
  }
}

/**
 * Put the app's Keychain session back before the web decides whether anyone is signed in.
 *
 * The same ordering rule as `hydrateDesktopSession`: `AuthStore.initialize()` answers "signed out"
 * from empty slots without a request, so this is awaited in `index.tsx` before the first render. A
 * session already in the slots wins, because it may be newer than the vault's copy — but only when
 * it is the same person's.
 *
 * F-0534 (I-1253): the Keychain says who is signed in to the app. When it names another person than
 * the slots, the slots are the last person's (a pairing while they were signed in, a session filed
 * at launch), and left in place they would sign the web in as them while the app's vault, push and
 * restored places are the new person's. The vault's session replaces them, and the owner rule then
 * drops the last person's data on the person change.
 */
export async function hydrateShellSession(): Promise<IShellHydrationOutcome> {
  if (!hasShellCapability('sessionVault')) return { outcome: 'not-shell' };

  const slots = deploymentCredentialSlots(effectiveDeploymentAddress());
  let present: string | null = null;
  try {
    present = localStorage.getItem(slots.tokenKey);
    if (!present && holdsCurrentRefreshSecret(slots.refreshKey))
      return { outcome: 'already-present' };
  } catch {
    return { outcome: 'already-present' };
  }

  const stored = await readShellSession();
  if (present) {
    const presentOwner = tokenOwner(present);
    const storedOwner = tokenOwner(stored?.accessToken);
    if (!stored || !presentOwner || !storedOwner || presentOwner === storedOwner) {
      return { outcome: 'already-present' };
    }
  }
  if (!stored?.accessToken) return { outcome: 'none-stored' };

  try {
    localStorage.setItem(slots.tokenKey, stored.accessToken);
  } catch {
    return { outcome: 'none-stored' };
  }
  // I-1625 §7: the Keychain holds it plain, encrypted by the operating system; the webview's copy is
  // wrapped like a browser's. Awaited, so the slot is stored before the app mounts.
  if (stored.refreshToken) await writeCurrentRefreshSecret(slots.refreshKey, stored.refreshToken);
  else removeCurrentRefreshSecret(slots.refreshKey);
  // T-0666: the Keychain's candidates come back with the session, so a lost answer before the web
  // view's storage went still recovers. I-1625: a replaced session's candidates were chosen for the
  // last person's secret, so they are always replaced by the vault's — none, when it holds none.
  await restorePendingRefreshSecrets(slots.refreshKey, stored.pendingRefreshTokens);
  return { outcome: present ? 'replaced' : 'restored' };
}
